import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
    FaStore,
    FaWarehouse,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaTruck,
    FaBoxes,
    FaIndustry,
    FaClock,
    FaUsers,
    FaShieldAlt,
    FaAward,
    FaTools,
    FaCheckCircle,
    FaArrowRight,
    FaPhone,
    FaEnvelope
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ShopInfo = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const navigate = useNavigate();

    // Navigation handler functions
    const handleVisitShops = () => {
        navigate('/contact'); // Navigate to contact page with shop location details
    };

    const handleContactUs = () => {
        navigate('/contact'); // Navigate to contact page
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.12,
                duration: 0.8
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const statsVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    const facilities = [
        {
            icon: FaStore,
            title: "3 Retail Shops",
            description: "Strategically located in Al-Khobar for easy access",
            color: "bg-blue-500"
        },
        {
            icon: FaWarehouse,
            title: "2 Large Warehouses",
            description: "Extensive storage capacity for bulk inventory",
            color: "bg-green-500"
        },
        {
            icon: FaBoxes,
            title: "Inventory Management",
            description: "Professional inventory management and distribution",
            color: "bg-purple-500"
        }
    ];

    const keyStats = [
        {
            number: "2004",
            label: "Established Since",
            icon: FaCalendarAlt
        },
        {
            number: "20+",
            label: "Years Experience",
            icon: FaAward
        },
        {
            number: "5",
            label: "Facilities",
            icon: FaStore
        },
        {
            number: "All KSA",
            label: "Coverage Area",
            icon: FaMapMarkerAlt
        }
    ];

    const services = [
        {
            icon: FaTruck,
            title: "Nationwide Delivery",
            description: "We supply materials all around Saudi Arabia with reliable logistics network"
        },
        {
            icon: FaClock,
            title: "Quick Turnaround",
            description: "Efficient processing ensures timely delivery for urgent requirements"
        },
        {
            icon: FaShieldAlt,
            title: "Quality Assurance",
            description: "All materials undergo strict quality checks before dispatch"
        },
        {
            icon: FaUsers,
            title: "Expert Support",
            description: "Professional team with decades of industrial experience during working hours"
        }
    ];

    return (
        <section className="py-20 md:py-28 bg-white relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-200/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }}></div>
            </div>

            <div className="container mx-auto px-4 md:px-8 relative z-10">
                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-16 md:mb-20"
                >
                    <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-secondary px-5 py-3 rounded-full mb-8 shadow-lg">
                        <FaIndustry className="text-primary text-base" />
                        <span className="text-primary font-bold text-sm uppercase tracking-wider">
                            About AKTP Business
                        </span>
                    </motion.div>

                    <motion.h2
                        variants={itemVariants}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight"
                    >
                        AKTP
                        <span className="block text-secondary drop-shadow-lg">Your Industrial Partner</span>
                    </motion.h2>

                    <motion.p
                        variants={itemVariants}
                        className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        Since 2004, we've been Saudi Arabia's trusted source for industrial materials, with extensive facilities in Al-Khobar and nationwide delivery capabilities
                    </motion.p>
                </motion.div>

                {/* Key Stats */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
                >
                    {keyStats.map((stat, index) => (
                        <motion.div
                            key={index}
                            variants={statsVariants}
                            className="bg-white rounded-2xl p-6 text-center shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 group"
                        >
                            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary transition-colors duration-300">
                                <stat.icon className="text-white text-2xl group-hover:text-primary" />
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-primary mb-2">{stat.number}</h3>
                            <p className="text-gray-600 font-medium">{stat.label}</p>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Our Facilities Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-20"
                >
                    <motion.h3
                        variants={itemVariants}
                        className="text-3xl md:text-4xl font-bold text-center text-primary mb-12"
                    >
                        Our Facilities
                    </motion.h3>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {facilities.map((facility, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                whileHover={{ y: -8 }}
                                className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500"
                            >
                                <div className="text-center">
                                    <div className={`w-16 h-16 ${facility.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        <facility.icon className="text-white text-2xl" />
                                    </div>
                                    <h4 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-primary transition-colors duration-300">
                                        {facility.title}
                                    </h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        {facility.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Heritage Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-20"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-primary rounded-3xl p-8 md:p-12 text-white shadow-2xl"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center">
                                        <FaAward className="text-primary text-2xl" />
                                    </div>
                                    <div className="bg-gradient-to-r from-secondary to-yellow-400 text-primary px-4 py-2 rounded-full text-sm font-bold">
                                        20+ Years Legacy
                                    </div>
                                </div>

                                <h3 className="text-3xl md:text-4xl font-bold mb-6">Two Decades of Excellence</h3>
                                <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                                    Since our establishment in 2004, AKTP has grown from a single shop to become the Eastern Province's leading industrial supplier, serving clients across the entire Kingdom of Saudi Arabia.
                                </p>

                                <div className="space-y-4">
                                    {["Started with a vision in 2004", "Expanded to 3 retail locations", "Added 2 major warehouses", "Nationwide delivery network"].map((milestone, idx) => (
                                        <motion.div
                                            key={idx}
                                            className="flex items-center gap-3"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.1 + 0.5 }}
                                        >
                                            <div className="w-3 h-3 bg-secondary rounded-full"></div>
                                            <span className="text-blue-100 font-medium">{milestone}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative">
                                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                                    <img
                                        src="/images/building_solutions.webp"
                                        alt="AKTP Facility"
                                        className="w-full h-64 object-cover rounded-2xl shadow-lg mb-6"
                                    />
                                    <div className="text-center">
                                        <div className="text-secondary text-lg font-bold mb-2">Al-Khobar, Saudi Arabia</div>
                                        <div className="text-blue-100">Strategically Located in Eastern Province</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Services Grid */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-20"
                >
                    <motion.h3
                        variants={itemVariants}
                        className="text-3xl md:text-4xl font-bold text-center text-primary mb-12"
                    >
                        Why Choose Us
                    </motion.h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                            >
                                <div className="flex items-start gap-6">
                                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-secondary transition-colors duration-300">
                                        <service.icon className="text-white text-2xl group-hover:text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-primary transition-colors duration-300">
                                            {service.title}
                                        </h4>
                                        <p className="text-gray-600 leading-relaxed">
                                            {service.description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* CTA Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-primary rounded-3xl p-10 md:p-12 shadow-2xl"
                    >
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Experience 20 Years of Excellence
                        </h3>
                        <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                            Partner with AKTP for reliable industrial solutions backed by two decades of experience and nationwide reach across Saudi Arabia.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleVisitShops} // Add navigation logic
                                className="bg-secondary text-primary px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 shadow-xl hover:bg-yellow-400 transition-all duration-300 cursor-pointer"
                            >
                                <FaStore />
                                Visit Our Shops
                                <FaArrowRight />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleContactUs} // Add navigation logic
                                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer"
                            >
                                <FaPhone />
                                Contact Us Today
                            </motion.button>
                        </div>

                        <div className="mt-8 pt-8 border-t border-gray-700">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-400">
                                <div className="flex items-center gap-2">
                                    <FaClock />
                                    <span>Saturday-Thursday: 8AM-1PM, 4PM-8:30PM</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default ShopInfo;